import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink, Loader2, Music2, Film, Radio, Tv, Sparkles } from "lucide-react";
import type { VideoType } from "@/lib/room-state";
import { RoomStore } from "@/lib/room-store";

interface NowPlayingPreviewProps { roomId: string; url: string; type: VideoType; }
interface PreviewData { title: string | null; author: string | null; thumbnail: string | null; provider: string; url: string; }
const PROVIDER_ICON: Record<string, typeof Music2> = { YouTube: Music2, Vimeo: Film, Twitch: Tv, HLS: Radio, Direct: Film };

export default function NowPlayingPreview({ roomId, url, type }: NowPlayingPreviewProps) {
  const [data, setData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    Promise.resolve().then(() => { if (cancelled) return; setLoading(true); setData(null); });
    RoomStore.fetchPreview(url).then((d: PreviewData) => { if (!cancelled) setData(d); }).catch(() => { if (!cancelled) setData({ title: null, author: null, thumbnail: null, provider: type, url }); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [roomId, url, type]);
  if (!url) return null;
  const ProviderIcon = PROVIDER_ICON[data?.provider || type] || Film;
  return (
    <AnimatePresence mode="wait">
      <motion.div key={url + (data?.title || "loading")} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }} className="flex items-center gap-3 px-4 py-3 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-white/[0.04] via-transparent to-white/[0.02] pointer-events-none" />
 <div className="w-10 h-10 flex items-center justify-center shrink-0 overflow-hidden relative">
          {loading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : data?.thumbnail ? <img src={data.thumbnail} alt="" className="w-full h-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} /> : <ProviderIcon className="w-4 h-4 text-primary" />}
        </div>
        <div className="min-w-0 flex-1 relative">
          <div className="text-xs font-semibold truncate flex items-center gap-1.5">
            {loading ? "Fetching preview…" : data?.title || (type === "direct" ? "Direct video stream" : `${type} stream`)}
            {!loading && <span className="hidden sm:inline-flex glass-subtle no-border rounded-full px-1.5 py-0.5 text-[9px]"><Sparkles className="w-2.5 h-2.5" /> Now playing</span>}
          </div>
          <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5 mt-0.5">
            <span className="inline-flex items-center gap-1 glass-subtle px-1.5 py-0.5 rounded-full"><ProviderIcon className="w-2.5 h-2.5" />{data?.provider || type}</span>
            {data?.author && <><span>·</span><span className="truncate">{data.author}</span></>}
          </div>
        </div>
 <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0 w-8 h-8 flex items-center justify-center transition-all relative">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </motion.div>
    </AnimatePresence>
  );
}
